import React, { useEffect, useContext, useState } from "react";
import Layout from "./components/Layout";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Login from "./pages/login";
import PrivateRoute from "./helpers/PrivateRoute";
import Signup from "./pages/signup";
import VerifyEmail from "./pages/VerifyEmail";
import BorrowerProfile from "./pages/borrowerProfile";
import Landing from "./pages/landing";
import Clientlogin from "./pages/clientlogin";
import ClientSignup from "./pages/clientsignup";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PDFDebugger from "./components/PDFDebugger";
import ThreeCXTest from "./pages/ThreeCXTest";
import PasswordChangeModal from "./components/PasswordChangeModal";
import AIChatbot from "./components/AIChatbot";
import "antd/dist/antd.css";
import "jspreadsheet-ce/dist/jspreadsheet.css"; // Excel-like grid styles
import "jsuites/dist/jsuites.css"; // jspreadsheet dependency styles
import "./theme.css"; // Import theme styles
import "./responsive.css"; // Import responsive styles
import PaymentSuccess from "./pages/paymentSuccess";
import { AppContext } from "./context/AppContext";
import { TourProvider } from "./context/TourContext"; // Import TourProvider
import { DiscussionNotificationProvider } from "./components/DiscussionNotificationProvider";
import { message } from "antd";
import ErrorBoundary from "./components/ErrorBoundary";

function AppContent() {
  const { theme, skin, user, setUser } = useContext(AppContext);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Remove existing skin and theme classes
    document.body.classList.remove(
      "skin-black",
      "skin-blue",
      "skin-yellow",
      "skin-green",
      "skin-red",
      "skin-purple",
      "skin-black-light",
      "skin-blue-light",
      "skin-yellow-light",
      "skin-green-light",
      "skin-red-light",
      "skin-purple-light",
      "dark-mode",
      "light-mode"
    );

    // Add skin class
    document.body.classList.add(skin);

    // Add theme class
    document.body.classList.add(theme === "dark" ? "dark-mode" : "light-mode");

    // Configure message notifications with higher z-index
    message.config({
      top: 20,
      duration: 6,
      maxCount: 3,
      prefixCls: "ant-message",
      getContainer: () => document.body,
    });
  }, [theme, skin]);

  // Check if user needs to change password
  useEffect(() => {
    if (user && user.needs_password_change === true) {
      setShowPasswordModal(true);
    }
  }, [user]);

  const handlePasswordChanged = () => {
    setShowPasswordModal(false);
    // Update user context to reflect password change
    setUser({ ...user, needs_password_change: false });
    message.success("Password changed successfully! You can now continue using the application.");
  };

  // Define public routes where chatbot should be hidden
  const publicRoutes = ["/", "/app", "/login", "/clientlogin", "/clientsignup", "/signup", "/verify-email", "/paysuccess", "/forgot-password", "/reset-password"];
  const showChatbot = user && !publicRoutes.includes(location.pathname);

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/app" element={<Landing />} />
        <Route path="/paysuccess" element={<PaymentSuccess />} />
        <Route path="/login" element={<Login />} />
        <Route path="/clientlogin" element={<Clientlogin />} />
        <Route path="/clientsignup" element={<ClientSignup />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* Debug route - accessible without authentication */}
        <Route path="/pdf-debug" element={<PDFDebugger />} />
        <Route path="/3cx-test" element={<ThreeCXTest />} />

        <Route
          path="/*"
          element={
            <PrivateRoute
              allowedRoles={[
                "Administrator",
                "Branch Manager",
                "Loan Officer",
                "Legal Officer",
                "Credit Manager",
                "Collections Manager",
                "Accountant",
                "Rider",
              ]}
            >
              <Layout />
            </PrivateRoute>
          }
        />

        <Route
          path="/borrowerProfile"
          element={
            <PrivateRoute
              allowedRoles={[
                "Borrower",
                "ab3c07d3-a225-4b68-8454-96e00116e307",
              ]}
            >
              <BorrowerProfile />
            </PrivateRoute>
          }
        />
      </Routes>

      {/* Password Change Modal - shown when user needs to change password */}
      <PasswordChangeModal
        visible={showPasswordModal}
        userId={user?.user_id}
        onPasswordChanged={handlePasswordChanged}
      />

      {/* AI Chatbot - shown for authenticated users, hidden on public routes */}
      {showChatbot && <AIChatbot />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <TourProvider>
        <DiscussionNotificationProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </DiscussionNotificationProvider>
      </TourProvider>
    </ErrorBoundary>
  );
}

export default App;
